import { z } from "zod";

/** The customers sidebar round (brief docs/briefs/customers-sidebar-round.md with "After the red team's review"; report
 *  docs/reviews/customers-sidebar-round-red-team-report.md, findings 1 to 4): the Customers screen's views column as each person's own layout,
 *  beside the inbox's (`sidebar.ts`). Its own record, marked `area: "customers"`, so every shared rule tells the two columns apart by the mark
 *  and never by guessing from the kinds; the inbox's record stays exactly as it was.
 *
 *  In plain words: the base is today's three segments under "Customers" plus a group "Tags" holding every customer tag of the workspace (the
 *  first 500 by name; the pool offers the rest). A person's record is stored only once they change something
 *  (`member_settings.customers_sidebar`, migration 0119) and is laid over the base on every read (`resolveSidebar`), so a new customer tag
 *  reaches everyone and what they removed on purpose stays out. */

/** what an entry of the Customers column can be: a built-in segment, a customer tag (the people who carry it), a project (its customers) */
export const CUSTOMERS_ITEM_KINDS = ["segment", "customerTag", "project"] as const;
export type CustomersItemKind = (typeof CUSTOMERS_ITEM_KINDS)[number];
export const CUSTOMERS_BUILT_INS = ["customers", "tags"] as const;
export type CustomersBuiltIn = (typeof CUSTOMERS_BUILT_INS)[number];
export const CUSTOMERS_BUILT_IN_NAME: Record<CustomersBuiltIn, string> = { customers: "Customers", tags: "Tags" };
/** limits (finding 4): the inbox's name and group limits, but 500 entries per group and 500 removals, so a workspace with a few hundred customer
 *  tags can still save its column and remove tags from it */
export const CUSTOMERS_SIDEBAR_LIMITS = Object.freeze({ nameMax: 40, groups: 30, itemsPerGroup: 500, removed: 500 });

/** the built-in segments of the Customers list, in the column's order, with their words */
export const CUSTOMER_SEGMENTS = [
  { id: "all", label: "All customers", icon: "users" },
  { id: "with-license", label: "With a license", icon: "verified" },
  { id: "without-license", label: "Without a license", icon: "help" },
] as const;
export type CustomerSegmentId = (typeof CUSTOMER_SEGMENTS)[number]["id"];
export const isCustomerSegment = (id: string): id is CustomerSegmentId => CUSTOMER_SEGMENTS.some((s) => s.id === id);

const ItemSchema = z.object({ kind: z.enum(CUSTOMERS_ITEM_KINDS), id: z.string().min(1).max(100), hidden: z.boolean() }).strict();
const GroupSchema = z.object({
  id: z.string().min(1).max(100),
  name: z.string().trim().min(1).max(CUSTOMERS_SIDEBAR_LIMITS.nameMax),
  builtIn: z.enum(CUSTOMERS_BUILT_INS).optional(),
  hidden: z.boolean(),
  folded: z.boolean(),
  items: z.array(ItemSchema).max(CUSTOMERS_SIDEBAR_LIMITS.itemsPerGroup),
}).strict();
/** a removal on purpose: `segment:<id>`, `customerTag:<id>` or `group:<customers|tags>` (finding 3: never the inbox's `tag:` kind) */
const RemovedSchema = z.string().regex(/^(segment:[a-z0-9-]{1,100}|customerTag:[A-Za-z0-9_-]{1,100}|group:(customers|tags))$/);
/** `member_settings.customers_sidebar`: version 1, always marked as the Customers column */
export const CustomersSidebarSchema = z.object({
  v: z.literal(1),
  area: z.literal("customers"),
  groups: z.array(GroupSchema).max(CUSTOMERS_SIDEBAR_LIMITS.groups),
  removed: z.array(RemovedSchema).max(CUSTOMERS_SIDEBAR_LIMITS.removed).optional(),
}).strict();
export type CustomersSidebarItem = z.infer<typeof ItemSchema>;
export type CustomersSidebarGroup = z.infer<typeof GroupSchema>;
export type CustomersSidebar = z.infer<typeof CustomersSidebarSchema>;

/** the customer tags the base lists: customer tags only (ticket tags never), by name in JavaScript's order (never the database's collation, so
 *  the server and the web build the same column), at most 500 */
function baseTagIds(tags: readonly { id: string; name: string; kind?: string }[]): string[] {
  return tags.filter((t) => t.kind === undefined || t.kind === "customer").slice().sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, CUSTOMERS_SIDEBAR_LIMITS.itemsPerGroup).map((t) => t.id);
}

/** Today's Customers column plus the Tags group: the base every person starts from. A fresh copy each time. */
export function defaultCustomersSidebar(tags: readonly { id: string; name: string; kind?: string }[]): CustomersSidebar {
  const group = (builtIn: CustomersBuiltIn, items: CustomersSidebarItem[]): CustomersSidebarGroup => ({ id: builtIn, name: CUSTOMERS_BUILT_IN_NAME[builtIn], builtIn, hidden: false, folded: false, items });
  return { v: 1, area: "customers", groups: [
    group("customers", CUSTOMER_SEGMENTS.map((s) => ({ kind: "segment", id: s.id, hidden: false }))),
    group("tags", baseTagIds(tags).map((id) => ({ kind: "customerTag", id, hidden: false }))),
  ] };
}
